// Users page — paginated list + inline role-picker + inline plan-picker per row.
//
// PX-73: the role-picker options come from a live `/admin/roles`
// fetch (not a hardcoded constant). Promotions to privileged roles
// trigger a confirmation modal; same-role pick is a no-op. Self-row
// dropdown is disabled (backend still enforces CANNOT_REVOKE_SELF).
//
// PX-119: same inline-picker shape for the subscription plan. Options
// come from `/admin/catalog/plans`; confirmation modal explains the
// `expires_at` semantic before the PATCH fires. Self-edit IS allowed
// for plan (unlike role) — admin moving themselves to VIP is a normal
// support flow.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { APIError, request } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { hasPerm } from '@/lib/perms'
import { PERM } from '@/types/api'
import { SubscriptionPicker, type SubscriptionChange } from '@/components/SubscriptionPicker'
import { Toggle } from '@/components/FormControls'
import type {
  AdminPlanResponse,
  AdminSubscriptionProductResponse,
  AdminUserCreate,
  AdminUserDetail,
  AdminUserListItem,
  AdminUserListResponse,
  AdminUserRoleSummary,
  Role,
  RoleResponse,
} from '@/types/api'

interface PendingChange {
  user: AdminUserListItem
  newRole: Role
}

interface PendingSubChange {
  user: AdminUserListItem
  change: SubscriptionChange
}

export function UsersPage() {
  const queryClient = useQueryClient()
  const { profile } = useAuth()
  const [search, setSearch] = useState('')
  const [cursor, setCursor] = useState<string | null>(null)
  const [pending, setPending] = useState<PendingChange | null>(null)
  const [pendingSub, setPendingSub] = useState<PendingSubChange | null>(null)

  const listQuery = useQuery({
    queryKey: ['admin', 'users', { search, cursor }],
    queryFn: () =>
      request<AdminUserListResponse>('/admin/users', {
        query: { search, cursor: cursor ?? undefined, limit: 25 },
      }),
  })

  // PX-73: role catalog from the live table — any custom role created
  // in /admin/roles is immediately available here. Cached 30s by
  // TanStack Query (matches the backend role_cache TTL).
  const rolesQuery = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => request<RoleResponse[]>('/admin/roles'),
    staleTime: 30_000,
  })
  const roleBySlug = new Map((rolesQuery.data ?? []).map((r) => [r.slug, r]))

  // PX-119: subscription plan catalog for the inline plan-picker.
  // Plans change rarely (catalog row is admin-edited via the Catalog
  // page); cache 5min so the picker stays responsive without per-row
  // refetches. Only `is_active` plans are offered as new picks (the
  // current value still renders even if inactive, so existing
  // assignments don't become invisible).
  const plansQuery = useQuery({
    queryKey: ['admin', 'catalog', 'plans'],
    queryFn: () => request<AdminPlanResponse[]>('/admin/catalog/plans'),
    staleTime: 5 * 60_000,
  })
  // PX-204: subscription-product catalog so the inline picker can offer the
  // exact SKUs (VIP weekly/monthly/quarterly), not just the tier.
  const productsQuery = useQuery({
    queryKey: ['admin', 'catalog', 'subscription-products'],
    queryFn: () => request<AdminSubscriptionProductResponse[]>('/admin/catalog/subscription-products'),
    staleTime: 5 * 60_000,
  })

  const setRoleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) =>
      request<AdminUserRoleSummary>(`/admin/users/${id}/role`, {
        method: 'PATCH',
        body: { role },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] })
      setPending(null)
    },
  })

  // PX-204: subscription change PATCH — body is the picker's fragment
  // (`{ product_id }` for a SKU, or `{ plan_id, unset_product_id }` for Free).
  // The service-layer split routes it through admin_set_subscription; credits /
  // expires_at are untouched. Response (`AdminUserDetail`) discarded — we
  // invalidate the list and re-render with the fresh state.
  const setSubMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: SubscriptionChange['patch'] }) =>
      request<unknown>(`/admin/users/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      setPendingSub(null)
    },
  })

  // PX-204: inline token-balance edit from the expandable Tokens cell. Writes
  // both buckets in one PATCH; no confirm modal (numeric edit, not a tier flip).
  const setTokensMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { allowance_balance: number; credits_balance: number } }) =>
      request<unknown>(`/admin/users/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })

  // PX-431: enable/disable account straight from the list row. Same PATCH the
  // detail panel uses; confirm() guards the disable (a stray tap mustn't lock a
  // user out). Backend toggles `users.is_active`.
  const setActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      request<unknown>(`/admin/users/${id}`, { method: 'PATCH', body: { is_active } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })

  // Admin creates a new user (backend POST /admin/users → register flow). Role
  // stays `user`; change it via the row dropdown afterwards.
  const [showCreate, setShowCreate] = useState(false)
  const canCreate = hasPerm(profile, PERM.users_write)
  const createMutation = useMutation({
    mutationFn: (body: AdminUserCreate) =>
      request<AdminUserDetail>('/admin/users', { method: 'POST', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      setShowCreate(false)
    },
  })

  const handleRolePick = (user: AdminUserListItem, newRole: Role) => {
    if (newRole === user.role) return
    setPending({ user, newRole })
  }

  const handleSubPick = (user: AdminUserListItem, change: SubscriptionChange) => {
    setPendingSub({ user, change })
  }

  return (
    <div className="p-6">
      <header className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-ink-400">
            Pick a role from the dropdown to grant or revoke. Self-revoke is blocked.
          </p>
        </div>
          <div className="flex items-center gap-3">
          <input
            type="search"
            placeholder="Search email…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCursor(null) }}
            className="w-64 rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
          {canCreate && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="shrink-0 rounded-md bg-brand-500 px-3 py-2 text-sm font-semibold text-white transition hover:bg-brand-600"
            >
              + Create user
            </button>
          )}
        </div>
      </header>

      {showCreate && (
        <CreateUserModal
          submitting={createMutation.isPending}
          error={createMutation.error instanceof APIError ? createMutation.error : null}
          onCancel={() => { setShowCreate(false); createMutation.reset() }}
          onSubmit={(body) => createMutation.mutate(body)}
        />
      )}

      {setRoleMutation.error instanceof APIError && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {setRoleMutation.error.message}
        </div>
      )}

      {setSubMutation.error instanceof APIError && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {setSubMutation.error.message}
        </div>
      )}

      {setTokensMutation.error instanceof APIError && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {setTokensMutation.error.message}
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-ink-100 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-ink-100 bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-400">
            <tr>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Subscription</th>
              <th className="px-4 py-3" title="Renewable allowance + purchased (never-expiring) tokens — click to edit">Tokens</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3 text-right" title="Total generations by this user">Gens</th>
              <th className="px-4 py-3 text-right">Active</th>
            </tr>
          </thead>
          <tbody>
            {listQuery.isLoading && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-ink-400">Loading…</td></tr>
            )}
            {listQuery.data?.items.map((u) => {
              const isSelf = profile?.id === u.id
              return (
                <tr key={u.id} className={`border-b border-ink-100 last:border-0 ${isSelf ? 'bg-brand-50/50' : ''}`}>
                  <td className="px-4 py-3">
                    <Link
                      to={`/users/${u.id}`}
                      className="font-medium text-brand-600 hover:underline"
                    >
                      {u.email ?? u.id}
                    </Link>
                    {u.full_name && <div className="text-xs text-ink-400">{u.full_name}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <SubscriptionPicker
                      productId={u.product_id}
                      planId={u.plan_id}
                      products={productsQuery.data ?? []}
                      plans={plansQuery.data ?? []}
                      onChange={(change) => handleSubPick(u, change)}
                      disabled={setSubMutation.isPending || plansQuery.isLoading || productsQuery.isLoading}
                    />
                  </td>
                  <td className="px-4 py-3 align-top">
                    <TokensCell
                      user={u}
                      onSave={(id, body) => setTokensMutation.mutate({ id, body })}
                      saving={setTokensMutation.isPending}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <RolePicker
                      value={u.role}
                      roles={rolesQuery.data ?? []}
                      onChange={(newRole) => handleRolePick(u, newRole)}
                      disabled={isSelf || setRoleMutation.isPending || rolesQuery.isLoading}
                      title={isSelf ? "Can't change your own role" : undefined}
                    />
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-ink-600">
                    {u.generation_count}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end">
                      <Toggle
                        checked={u.is_active}
                        disabled={!!u.deleted_at || isSelf || setActiveMutation.isPending}
                        onChange={(v) => {
                          if (!v && !confirm(`Disable ${u.email ?? u.id}? They won't be able to use the app until re-enabled.`)) return
                          setActiveMutation.mutate({ id: u.id, is_active: v })
                        }}
                      />
                    </div>
                  </td>
                </tr>
              )
            })}
            {!listQuery.isLoading && listQuery.data?.items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-ink-400">No users match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {listQuery.data?.has_more && listQuery.data.cursor && (
        <div className="mt-4 text-right">
          <button
            type="button"
            onClick={() => setCursor(listQuery.data!.cursor!)}
            className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:border-ink-300"
          >
            Next page →
          </button>
        </div>
      )}

      {pending && (
        <RoleChangeConfirmModal
          user={pending.user}
          newRole={pending.newRole}
          newRoleInfo={roleBySlug.get(pending.newRole) ?? null}
          currentRoleInfo={roleBySlug.get(pending.user.role) ?? null}
          pending={setRoleMutation.isPending}
          onConfirm={() => setRoleMutation.mutate({ id: pending.user.id, role: pending.newRole })}
          onCancel={() => setPending(null)}
        />
      )}

      {pendingSub && (
        <SubscriptionChangeConfirmModal
          user={pendingSub.user}
          label={pendingSub.change.label}
          toFree={!!pendingSub.change.patch.unset_product_id}
          pending={setSubMutation.isPending}
          onConfirm={() =>
            setSubMutation.mutate({ id: pendingSub.user.id, patch: pendingSub.change.patch })
          }
          onCancel={() => setPendingSub(null)}
        />
      )}
    </div>
  )
}

// ----- Role picker --------------------------------------------------

function RolePicker({
  value,
  roles,
  onChange,
  disabled,
  title,
}: {
  value: Role
  roles: RoleResponse[]
  onChange: (next: Role) => void
  disabled?: boolean
  title?: string
}) {
  const palette = roleVisualPalette(roles.find((r) => r.slug === value) ?? null)
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as Role)}
      disabled={disabled}
      title={title}
      className={`rounded-md border px-2 py-1 text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-brand-100 ${palette} disabled:cursor-not-allowed disabled:opacity-60`}
    >
      {/* If the user's current slug isn't in the catalog (e.g. role
          row was just deleted server-side), still render it as an
          option so the dropdown doesn't switch silently. */}
      {!roles.some((r) => r.slug === value) && (
        <option value={value}>{value} (unknown)</option>
      )}
      {roles.map((r) => (
        <option key={r.id} value={r.slug}>
          {r.name}{r.is_system ? '' : ' ·'} {r.slug}
        </option>
      ))}
    </select>
  )
}

function roleVisualPalette(role: RoleResponse | null): string {
  if (!role) return 'border-ink-200 bg-white text-ink-700'
  // Wildcard or non-empty permissions count as privileged → tint.
  const isAdmin = role.permissions.includes('*')
  if (isAdmin) return 'border-brand-300 bg-brand-50 text-brand-700'
  if (role.permissions.length > 0) return 'border-amber-300 bg-amber-50 text-amber-800'
  return 'border-ink-200 bg-white text-ink-700'
}

// ----- Confirmation modal -------------------------------------------

function RoleChangeConfirmModal({
  user,
  newRole,
  newRoleInfo,
  currentRoleInfo,
  pending,
  onConfirm,
  onCancel,
}: {
  user: AdminUserListItem
  newRole: Role
  newRoleInfo: RoleResponse | null
  currentRoleInfo: RoleResponse | null
  pending: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const newPrivileged = !!newRoleInfo && (newRoleInfo.permissions.includes('*') || newRoleInfo.permissions.length > 0)
  const oldPrivileged = !!currentRoleInfo && (currentRoleInfo.permissions.includes('*') || currentRoleInfo.permissions.length > 0)
  const isPromotion = newPrivileged && !oldPrivileged
  const isDemotion = !newPrivileged && oldPrivileged
  const heading = isPromotion ? 'Grant role?' : isDemotion ? 'Demote user?' : 'Change role?'
  const newName = newRoleInfo?.name ?? newRole
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold tracking-tight">{heading}</h2>
        <p className="mt-2 text-sm text-ink-500">
          You're about to set{' '}
          <span className="font-medium text-ink-900">{user.email ?? user.id}</span>
          {' '}to <RoleTag role={newRoleInfo} fallbackSlug={newRole} />.
          {newRoleInfo && newRoleInfo.permissions.length > 0 && (
            <>
              {' '}They gain {newRoleInfo.permissions.includes('*') ? 'every' : newRoleInfo.permissions.length} permission{newRoleInfo.permissions.length === 1 ? '' : 's'}.
            </>
          )}
          {!newPrivileged && (
            <> They lose all admin access.</>
          )}
        </p>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:bg-ink-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className={`rounded-md px-3 py-1.5 text-sm font-semibold text-white transition disabled:opacity-60 ${
              isDemotion ? 'bg-red-500 hover:bg-red-600' : 'bg-brand-500 hover:bg-brand-600'
            }`}
          >
            {pending ? 'Saving…' : isPromotion ? `Grant ${newName}` : `Set ${newName}`}
          </button>
        </div>
      </div>
    </div>
  )
}

function RoleTag({ role, fallbackSlug }: { role: RoleResponse | null; fallbackSlug: string }) {
  const palette = roleVisualPalette(role)
  const label = role?.name ?? fallbackSlug
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 font-mono text-[11px] font-medium ${palette}`}>
      {label}
    </span>
  )
}

// ----- Subscription confirmation modal ------------------------------
// SubscriptionPicker lives in `@/components/SubscriptionPicker` so the
// UserDetail page (PX-204) reuses it + the Free→null mapping.

function SubscriptionChangeConfirmModal({
  user,
  label,
  toFree,
  pending,
  onConfirm,
  onCancel,
}: {
  user: AdminUserListItem
  label: string
  toFree: boolean
  pending: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold tracking-tight">
          {toFree ? 'Downgrade to Free?' : 'Change subscription?'}
        </h2>
        <p className="mt-2 text-sm text-ink-500">
          You're about to set{' '}
          <span className="font-medium text-ink-900">{user.email ?? user.id}</span>
          {' '}to <span className="font-medium text-ink-900">{label}</span>.
        </p>
        <p className="mt-3 rounded-md bg-ink-50 px-3 py-2 text-xs text-ink-500">
          {toFree
            ? 'Free has no SKU — the product is cleared. '
            : 'The plan tier is derived from the product. '}
          The user's <code className="font-mono">expires_at</code> and token
          balances are kept as-is — edit those on the user detail page.
        </p>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:bg-ink-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className={`rounded-md px-3 py-1.5 text-sm font-semibold text-white transition disabled:opacity-60 ${
              toFree ? 'bg-red-500 hover:bg-red-600' : 'bg-brand-500 hover:bg-brand-600'
            }`}
          >
            {pending ? 'Saving…' : `Set ${label}`}
          </button>
        </div>
      </div>
    </div>
  )
}

// ----- Tokens cell --------------------------------------------------
// PX-204: collapsed shows `allowance + purchased`; clicking expands an inline
// editor with both number inputs + Save/Cancel. Encapsulates its own input
// state so the page-level table stays simple.

function TokensCell({
  user,
  onSave,
  saving,
}: {
  user: AdminUserListItem
  onSave: (id: string, body: { allowance_balance: number; credits_balance: number }) => void
  saving: boolean
}) {
  const [open, setOpen] = useState(false)
  const [allowance, setAllowance] = useState(String(user.allowance_balance))
  const [credits, setCredits] = useState(String(user.credits_balance))

  // Re-sync local inputs whenever the row's persisted values change (after a
  // successful save the list refetches and the row re-renders).
  useEffect(() => {
    setAllowance(String(user.allowance_balance))
    setCredits(String(user.credits_balance))
  }, [user.allowance_balance, user.credits_balance])

  if (!open) {
    // Collapsed: one total (allowance + purchased). Tooltip keeps the split;
    // clicking expands into the two editable buckets below.
    const total = user.allowance_balance + user.credits_balance
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={`Allowance (renewable): ${user.allowance_balance}\nPurchased (never expires): ${user.credits_balance}\nClick to edit`}
        className="rounded-md px-1 py-0.5 tabular-nums text-ink-700 transition hover:bg-ink-50"
      >
        {total}
      </button>
    )
  }

  const aVal = Number(allowance)
  const cVal = Number(credits)
  const valid =
    Number.isInteger(aVal) && aVal >= 0 && Number.isInteger(cVal) && cVal >= 0

  const inputCls =
    'w-24 rounded-md border border-ink-200 px-2 py-1 text-xs tabular-nums focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100'

  return (
    <div className="flex flex-col gap-1.5">
      <label className="flex items-center gap-2 text-xs text-ink-500">
        <span className="w-20">Allowance</span>
        <input type="number" min={0} step={1} value={allowance}
          onChange={(e) => setAllowance(e.target.value)} className={inputCls} />
      </label>
      <label className="flex items-center gap-2 text-xs text-ink-500">
        <span className="w-20">Purchased</span>
        <input type="number" min={0} step={1} value={credits}
          onChange={(e) => setCredits(e.target.value)} className={inputCls} />
      </label>
      {!valid && (
        <p className="text-xs text-red-600">Both must be non-negative integers.</p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={!valid || saving}
          onClick={() => {
            onSave(user.id, { allowance_balance: aVal, credits_balance: cVal })
            setOpen(false)
          }}
          className="rounded-md bg-brand-500 px-2.5 py-1 text-xs font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
        >
          Save
        </button>
        <button
          type="button"
          onClick={() => {
            setAllowance(String(user.allowance_balance))
            setCredits(String(user.credits_balance))
            setOpen(false)
          }}
          className="rounded-md border border-ink-200 px-2.5 py-1 text-xs font-medium text-ink-700 transition hover:bg-ink-50"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

// --- Create-user modal ----------------------------------------------------

function generatePassword(length = 16): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'
  const bytes = new Uint32Array(length)
  crypto.getRandomValues(bytes)
  let out = ''
  for (let i = 0; i < length; i++) out += alphabet[bytes[i] % alphabet.length]
  return out
}

function CreateUserModal({
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  submitting: boolean
  error: APIError | null
  onSubmit: (body: AdminUserCreate) => void
  onCancel: () => void
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState(() => generatePassword())
  const [fullName, setFullName] = useState('')
  const [isActive, setIsActive] = useState(true)

  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())
  const passwordOk = password.length >= 8 && password.length <= 128
  const canSubmit = emailOk && passwordOk && !submitting

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-base font-semibold tracking-tight">Create user</h2>
        <p className="mt-1 text-xs text-ink-400">
          Creates an account via the standard registration flow. Role is <span className="font-medium">user</span> — change it from the row dropdown afterwards.
        </p>

        {error && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error.message}
          </div>
        )}

        <div className="mt-4 space-y-3">
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-ink-500">Email</span>
            <input
              type="email"
              value={email}
              autoFocus
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-ink-500">Password (≥ 8 chars — copy it, it can't be retrieved later)</span>
            <div className="flex gap-2">
              <input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border border-ink-200 px-3 py-2 font-mono text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
              />
              <button
                type="button"
                onClick={() => setPassword(generatePassword())}
                className="shrink-0 rounded-md border border-ink-200 px-2.5 py-1 text-xs font-medium text-ink-700 transition hover:bg-ink-50"
              >
                Generate
              </button>
              <button
                type="button"
                onClick={() => void navigator.clipboard?.writeText(password)}
                className="shrink-0 rounded-md border border-ink-200 px-2.5 py-1 text-xs font-medium text-ink-700 transition hover:bg-ink-50"
              >
                Copy
              </button>
            </div>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-ink-500">Full name (optional)</span>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          </label>

          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Active (uncheck to create a disabled account)
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:bg-ink-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onSubmit({
              email: email.trim(),
              password,
              full_name: fullName.trim() || null,
              is_active: isActive,
            })}
            className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
          >
            {submitting ? 'Creating…' : 'Create user'}
          </button>
        </div>
      </div>
    </div>
  )
}

